// src/pages/vendors/purchase-order-form-components/PODetailsSection.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  CalendarToday as CalendarIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const PODetailsSection = ({ purchaseOrder, onPOChange, canEdit = true }) => {
  const theme = useTheme();

  return (
    <Paper sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
    }}>
      <Typography variant="h6" sx={{ 
        mb: 3, 
        fontWeight: 500, 
        fontSize: '1.2rem',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AssignmentIcon sx={{ fontSize: 28 }} />
        Purchase Order Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="PO Number"
            fullWidth
            value={purchaseOrder.vendor_PO_number}
            onChange={(e) => onPOChange('vendor_PO_number', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="PO Date"
            value={purchaseOrder.vendor_PO_date ? dayjs(purchaseOrder.vendor_PO_date) : null}
            onChange={(date) => onPOChange('vendor_PO_date', date ? date.format('YYYY-MM-DD') : '')}
            disabled={!canEdit}
            slotProps={{
              textField: {
                fullWidth: true,
                InputProps: {
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    minHeight: 56,
                    '& input': {
                      py: 2.5,
                      px: 3,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                },
                sx: {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { border: 'none' }
                  }
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Expected Delivery"
            value={purchaseOrder.expected_delivery_date ? dayjs(purchaseOrder.expected_delivery_date) : null}
            onChange={(date) => onPOChange('expected_delivery_date', date ? date.format('YYYY-MM-DD') : '')}
            disabled={!canEdit}
            slotProps={{
              textField: {
                fullWidth: true,
                InputProps: {
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    minHeight: 56,
                    '& input': {
                      py: 2.5,
                      px: 3,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                },
                sx: {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { border: 'none' }
                  }
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth disabled={!canEdit}>
            <InputLabel>Status</InputLabel>
            <Select
              value={purchaseOrder.status}
              onChange={(e) => onPOChange('status', e.target.value)}
              label="Status"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& .MuiSelect-select': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

      </Grid>
    </Paper>
  );
};

export default PODetailsSection;